import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowUpRight, Clock, ArrowsClockwise, WarningCircle } from "@phosphor-icons/react";
import { FadeIn, Stagger, StaggerItem } from "../components/Motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(d) {
  try {
    return new Date(d).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

/** Skeleton card shown while loading */
function BlogSkeleton() {
  return (
    <div className="glass rounded-3xl overflow-hidden animate-pulse">
      <div className="h-56 bg-[#0b2e5e]/8" />
      <div className="p-6 space-y-3">
        <div className="h-3 w-20 bg-[#0b2e5e]/10 rounded-full" />
        <div className="h-6 w-3/4 bg-[#0b2e5e]/10 rounded-lg" />
        <div className="h-6 w-1/2 bg-[#0b2e5e]/10 rounded-lg" />
        <div className="h-4 w-full bg-[#0b2e5e]/8 rounded-lg mt-4" />
        <div className="h-4 w-4/5 bg-[#0b2e5e]/8 rounded-lg" />
      </div>
    </div>
  );
}

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPosts = useCallback(() => {
    setLoading(true);
    setError(null);
    axios
      .get(`${API}/blog`)
      .then((r) => setPosts(r.data))
      .catch((e) => {
        console.error(e);
        const msg =
          e?.response?.data?.detail ||
          "Couldn't load posts. Please check your connection and try again.";
        setError(msg);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  return (
    <div data-testid="blog-page">
      {/* ── Hero ── */}
      <section className="pt-36 pb-16 md:pt-44 text-center">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <FadeIn>
            <div className="flex items-center justify-center gap-6 mb-8">
              <div className="h-[1px] w-16 bg-[#0b2e5e]/20" />
              <span className="issue-stripe">§ 04 · The Journal</span>
              <div className="h-[1px] w-16 bg-[#0b2e5e]/20" />
            </div>
            <h1 className="font-display text-5xl sm:text-7xl lg:text-[108px] tracking-[-0.03em] font-semibold leading-[0.9] text-[#0b2e5e]">
              Writing on
              <br />
              <span className="font-serif-italic text-[#1d4ed8]">the craft,</span> the business,
              <br />
              <span className="font-serif-italic text-[#e11d48]">the future.</span>
            </h1>
          </FadeIn>
          <FadeIn delay={0.15}>
            <p className="mt-8 max-w-2xl mx-auto text-lg text-[#0b2e5e]/75 leading-relaxed">
              Short, honest essays on how we think — from digital marketing and brand strategy, to
              creative design and the trends shaping businesses tomorrow.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* ── Posts ── */}
      <section className="pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">

          {/* Loading skeletons */}
          {loading && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-loading">
              {[1, 2, 3].map((n) => <BlogSkeleton key={n} />)}
            </div>
          )}

          {/* Error state */}
          {!loading && error && (
            <div
              className="flex flex-col items-center justify-center py-28 gap-6 text-center"
              data-testid="blog-error"
            >
              <WarningCircle size={48} className="text-[#e11d48]/60" />
              <div>
                <p className="font-display text-2xl text-[#0b2e5e]">Couldn't load posts</p>
                <p className="text-[#0b2e5e]/55 mt-2 text-sm max-w-sm">{error}</p>
              </div>
              <button
                onClick={fetchPosts}
                className="btn-ghost inline-flex items-center gap-2"
                data-testid="blog-retry"
              >
                <ArrowsClockwise weight="bold" size={16} />
                Try again
              </button>
            </div>
          )}

          {/* Empty state — no error but no posts yet */}
          {!loading && !error && posts.length === 0 && (
            <div
              className="flex flex-col items-center justify-center py-28 gap-4 text-center"
              data-testid="blog-empty"
            >
              <p className="font-display text-3xl text-[#0b2e5e]/30">No posts yet.</p>
              <p className="text-[#0b2e5e]/40 text-sm">
                Check back soon — we write regularly.
              </p>
              <Link to="/" className="btn-ghost mt-2">Back to home</Link>
            </div>
          )}

          {/* Posts grid */}
          {!loading && !error && posts.length > 0 && (
            <Stagger className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((p, i) => (
                <StaggerItem key={p.id}>
                  <Link
                    to={`/blog/${p.slug}`}
                    className="group block h-full"
                    data-testid={`blog-card-${p.slug}`}
                  >
                    <article className="glass tracing-border rounded-3xl overflow-hidden h-full flex flex-col hover:-translate-y-1 transition-transform duration-500">
                      {/* Cover image */}
                      <div className="relative h-56 overflow-hidden bg-[#0b2e5e]/5">
                        {p.cover_image ? (
                          <img
                            src={p.cover_image}
                            alt={p.title}
                            className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-[1000ms]"
                          />
                        ) : (
                          <div className="h-full w-full flex items-center justify-center">
                            <span className="font-display text-7xl text-[#0b2e5e]/10 font-semibold">
                              {p.category?.charAt(0) || "M"}
                            </span>
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-[#FAF6EF] to-transparent opacity-60" />
                        <div className="absolute top-4 left-4 tag">{p.category}</div>
                      </div>

                      {/* Card body */}
                      <div className="p-6 flex-1 flex flex-col">
                        <div className="flex items-center gap-3 text-xs text-[#0b2e5e]/50 font-mono-mini">
                          <span>{formatDate(p.published_at)}</span>
                          <span className="inline-flex items-center gap-1">
                            <Clock size={12} /> {p.read_minutes} min read
                          </span>
                        </div>
                        <h3 className="font-display text-2xl md:text-3xl mt-4 leading-tight group-hover:text-[#e11d48] transition-colors duration-300">
                          {p.title}
                        </h3>
                        <p className="text-[#0b2e5e]/65 mt-3 text-sm line-clamp-3 flex-1">
                          {p.excerpt}
                        </p>
                        <div className="mt-6 flex items-center gap-2 text-[#0b2e5e]/55 text-sm group-hover:text-[#e11d48] transition-colors duration-300">
                          Read post <ArrowUpRight weight="bold" size={14} />
                        </div>
                      </div>
                    </article>
                  </Link>
                </StaggerItem>
              ))}
            </Stagger>
          )}
        </div>
      </section>
    </div>
  );
}
